import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import { Trans } from 'react-i18next';
import { OBTER_CONFIGURACAO_POR_NOME } from './queries';
import { ConfiguracaoEnum } from '../../../../shared/util/enums/configuracaoEnum';
import {
  formatValor,
  transformSecondsToHHmm
} from '../../../../shared/util/transfoms';
import { Tooltip } from '../../../ui';
import { InfoIcon } from '../../../ui/icons';
import i18n from '../../../../shared/util/i18n';
import styles from './PriceFooter.module.css';

function SebraetecFooter({ product }) {
  const ft = product.sebraetecFichaTecnica;
  const { data } = useQuery(OBTER_CONFIGURACAO_POR_NOME, {
    variables: {
      configuracaoNome: ConfiguracaoEnum.SEBRAETEC_PORCENTAGEM_SUBSIDIO
    },
    fetchPolicy: 'cache-first'
  });
  const subsidio =
    data &&
    data.obterConfiguracaoPorNome &&
    data.obterConfiguracaoPorNome.valor &&
    data.obterConfiguracaoPorNome.valor.valorNumber;

  return (
    <>
      <div className={styles.from}>
        <span className={styles.strikethrough}>
          {i18n.t('DE')} {formatValor(ft.preco, true)}
        </span>
      </div>
      <div className={styles.divider} />
      <div className={styles.subsidio}>
        <Trans
          i18nKey='SEBRAE_WILL_COVER_UP_TO'
          components={[<strong key='0' className={styles.strong} />]}
          values={{ value: subsidio }}
        />
        <Tooltip
          placement='top'
          className={styles.info}
          title={i18n.t('SEBRAETEC_SUBSIDY_ELIGIBILITY')}>
          <InfoIcon size={14} className={styles.infoIcon} />
        </Tooltip>
      </div>
      <div className={styles.payRow}>
        <span className={styles.priceLabel}>{i18n.t('PAY_UP_TO')}:</span>
        <span className={styles.price}>
          {formatValor(ft.preco * (1 - (subsidio || 0) / 100), true)}
        </span>
      </div>
    </>
  );
}

function ConsultoriaFooter({ product }) {
  const consultoria = product.consultoria || {};
  const valorMercado = consultoria.valorMercado || product.valorMercado;
  const valorHora = consultoria.valorHora || product.preco;

  return (
    <>
      {valorMercado ? (
        <>
          <div className={styles.from}>
            <span className={styles.strikethrough}>
              {i18n.t('DE')} {formatValor(valorMercado, true)}
            </span>
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      <div className={styles.priceLabel}>{i18n.t('IN_SEBRAEPLACE')}</div>
      <div className={styles.price}>
        {formatValor(valorHora, true)}
        <span className={styles.priceSuffix}>/{i18n.t('HOUR')}</span>
      </div>
    </>
  );
}

function SaasFooter({ product }) {
  const saas = product.saasProduto || {};
  const isFree = saas.preco === 0;

  return (
    <>
      {saas.precoMercado ? (
        <>
          <div className={styles.from}>
            <span className={styles.strikethrough}>
              {i18n.t('DE')} {formatValor(saas.precoMercado, true)}
            </span>
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {isFree ? (
        <div className={styles.requestContact}>{i18n.t('REQUEST_CONTACT')}</div>
      ) : (
        <>
          <div className={styles.priceLabel}>
            {i18n.t('SEBRAEPLACE_FROM_PRICE')}
          </div>
          <div className={styles.price}>{formatValor(saas.preco, true)}</div>
        </>
      )}
    </>
  );
}

function CursoFooter({ product }) {
  const curso = product.curso || {};
  const isFree = curso.isGratuito || curso.valor === 0;
  const valorFinal =
    curso.valorFinal && curso.valorFinal > 0 ? curso.valorFinal : null;
  const duration = curso.quantidadeSegundos
    ? transformSecondsToHHmm(curso.quantidadeSegundos)
    : null;

  return (
    <>
      {!isFree && curso.valorMercado ? (
        <>
          <div className={styles.from}>
            <span className={styles.strikethrough}>
              {i18n.t('DE')} {formatValor(curso.valorMercado, true)}
            </span>
          </div>
          <div className={styles.divider} />
        </>
      ) : null}
      {!isFree && valorFinal ? (
        <div className={styles.priceLabel}>
          {i18n.t('IN_SEBRAEPLACE')}{' '}
          <span className={styles.strikethrough}>
            {formatValor(curso.valor, true)}
          </span>
        </div>
      ) : null}
      <div className={styles.priceRow}>
        <span className={styles.price}>
          {isFree
            ? i18n.t('GRATUITOUS')
            : formatValor(valorFinal || curso.valor, true)}
        </span>
        {duration ? <span className={styles.duration}>{duration}</span> : null}
      </div>
    </>
  );
}

export default function PriceFooter({ product }) {
  return (
    <div className={styles.root}>
      {product.tipo === 'SEBRAETEC_FICHA_TECNICA' && (
        <SebraetecFooter product={product} />
      )}
      {product.tipo === 'CONSULTORIA' && (
        <ConsultoriaFooter product={product} />
      )}
      {product.tipo === 'SAAS_PRODUTO' && <SaasFooter product={product} />}
      {product.tipo === 'CURSO' && <CursoFooter product={product} />}
    </div>
  );
}

PriceFooter.propTypes = {
  product: PropTypes.shape({
    tipo: PropTypes.string.isRequired
  }).isRequired
};

SebraetecFooter.propTypes = { product: PropTypes.object.isRequired };
ConsultoriaFooter.propTypes = { product: PropTypes.object.isRequired };
SaasFooter.propTypes = { product: PropTypes.object.isRequired };
CursoFooter.propTypes = { product: PropTypes.object.isRequired };
